/**
 * PF MolView Symmetry Detection
 *
 * Detects topological symmetry in molecules by grouping atoms
 * with equivalent local environments.
 *
 * Adapted from VMap molecular-graph.ts algorithm.
 */

import type { RGBColor } from './colors.ts';
import {
  ATOMIC_NUMBERS,
  getElementPrime,
  getAtomicRadius,
  isHydrogen,
  BOND_THRESHOLD,
  MIN_BOND_DISTANCE,
} from './element-constants.ts';

// ============================================================================
// Types
// ============================================================================

/**
 * Position in 3D space
 */
export type Vec3 = [number, number, number];

/**
 * Atom data extracted from 3Dmol
 */
export interface AtomData {
  index: number;
  element: string;
  position: Vec3;
  formalCharge?: number;
  bonds?: Array<{ targetIndex: number; order: number }>;
}

/**
 * Internal atom descriptor for symmetry detection
 */
interface AtomDescriptor {
  index: number;
  element: string;
  atomicNumber: string;
  position: Vec3;
  numBondsNotToH: number;
  numBondsToH: number;
  ringInfo: number;
  oneThreeElem: number;
  oneThreeCount: number;
  oneFourElem: number;
  oneFourCount: number;
}

/**
 * A group of symmetry-equivalent atoms
 */
export interface SymmetryGroup {
  /** Indices of equivalent atoms */
  atomIndices: number[];
  /** Shared topological fingerprint */
  atomString: string;
  /** Assigned color for visualization */
  color: RGBColor;
  /** Group label (e.g., "C3" for 3 equivalent carbons) */
  label: string;
}

/**
 * Result of symmetry detection
 */
export interface SymmetryResult {
  /** Groups of equivalent atoms (size >= 2) */
  groups: SymmetryGroup[];
  /** Atoms with no equivalents */
  uniqueAtoms: number[];
  /** Total number of atoms analyzed */
  totalAtoms: number;
}

/**
 * Ring information for SSS Ring mode
 */
export interface RingInfo {
  /** List of rings, each containing atom indices */
  rings: number[][];
  /** Map from atom index to ring indices */
  atomToRings: Map<number, number[]>;
  /** Bonds that are part of rings (stored as "i-j" with i < j) */
  ringBonds: Set<string>;
}

// ============================================================================
// Symmetry Colors (from legacy molecularGraphics.py symColors)
// ============================================================================

/**
 * Color palette for symmetry groups (32 distinct colors)
 */
export const SYMMETRY_COLORS: RGBColor[] = [
  [0.75, 0.0, 0.0],    // Red
  [0.0, 0.0, 0.9],     // Blue
  [0.0, 0.75, 0.0],    // Green
  [0.75, 0.75, 0.0],   // Yellow
  [0.75, 0.0, 0.75],   // Magenta
  [0.75, 0.38, 0.0],   // Orange
  [0.0, 0.75, 0.75],   // Cyan
  [0.2, 0.3, 0.4],     // Dark blue-grey
  [0.65, 0.1, 0.0],    // Dark red
  [0.1, 0.0, 0.65],    // Dark blue
  [0.0, 0.65, 0.1],    // Dark green
  [0.65, 0.65, 0.2],   // Olive
  [0.65, 0.2, 0.65],   // Purple
  [0.65, 0.2, 0.1],    // Brown
  [0.2, 0.65, 0.65],   // Teal
  [0.1, 0.85, 0.1],    // Bright green
  [1.0, 0.0, 0.0],     // Bright red
  [0.0, 0.0, 1.0],     // Bright blue
  [0.0, 1.0, 0.0],     // Bright green
  [1.0, 0.75, 0.0],    // Gold
  [0.75, 0.0, 1.0],    // Violet
  [1.0, 0.38, 0.0],    // Bright orange
  [0.0, 1.0, 0.75],    // Aqua
  [0.4, 0.3, 0.2],     // Dark brown
  [0.85, 0.3, 0.0],    // Red-orange
  [0.3, 0.0, 0.85],    // Indigo
  [0.0, 0.85, 0.3],    // Spring green
  [0.85, 0.85, 0.4],   // Light olive
  [0.85, 0.4, 0.85],   // Light purple
  [0.85, 0.4, 0.3],    // Salmon
  [0.4, 0.85, 0.85],   // Light teal
  [0.3, 1.0, 0.3],     // Lime
];

/**
 * Default color for atoms not in any symmetry group
 */
export const DEFAULT_ATOM_COLOR: RGBColor = [0.75, 0.75, 0.75]; // Grey

// ============================================================================
// Symmetry Detection Algorithm
// ============================================================================

/**
 * Detect symmetry groups in a molecule
 *
 * @param atoms - Array of atom data from 3Dmol viewer
 * @param includeHydrogens - Whether to include H atoms in symmetry detection
 * @returns Symmetry detection results
 */
export function detectSymmetry(
  atoms: AtomData[],
  includeHydrogens: boolean = false
): SymmetryResult {
  // Filter atoms based on hydrogen inclusion
  const atomsToAnalyze = includeHydrogens
    ? atoms
    : atoms.filter(a => !isHydrogen(a.element));

  if (atomsToAnalyze.length === 0) {
    return { groups: [], uniqueAtoms: [], totalAtoms: 0 };
  }

  // Build molecular graph
  const descriptors = generateDescriptors(atomsToAnalyze);
  const adjacencyList = generateAdjacencyList(descriptors);

  // Count bonds
  countBonds(descriptors, adjacencyList);

  // Calculate ring info
  const ringInfo = calculateRingInfo(descriptors, adjacencyList);
  for (let i = 0; i < descriptors.length; i++) {
    descriptors[i].ringInfo = ringInfo[i];
  }

  // Calculate 1-3 and 1-4 products
  calculateOneThreeProducts(descriptors, adjacencyList);
  calculateOneFourProducts(descriptors, adjacencyList);

  // Generate atom strings
  const atomStrings = generateAtomStrings(descriptors, adjacencyList);

  // Group atoms by identical atom strings
  const stringToIndices = new Map<string, number[]>();
  for (let i = 0; i < atomStrings.length; i++) {
    const str = atomStrings[i];
    const originalIndex = descriptors[i].index;
    if (!stringToIndices.has(str)) {
      stringToIndices.set(str, []);
    }
    stringToIndices.get(str)!.push(originalIndex);
  }

  // Build symmetry groups (only groups with 2+ atoms)
  const groups: SymmetryGroup[] = [];
  const uniqueAtoms: number[] = [];
  let colorIndex = 0;

  for (const [atomString, indices] of stringToIndices) {
    if (indices.length >= 2) {
      // Get element for label
      const element = atoms[indices[0]]?.element ?? '?';
      const label = `${element}${indices.length}`;

      groups.push({
        atomIndices: indices,
        atomString,
        color: SYMMETRY_COLORS[colorIndex % SYMMETRY_COLORS.length],
        label,
      });
      colorIndex++;
    } else {
      uniqueAtoms.push(indices[0]);
    }
  }

  // Sort groups by size (largest first)
  groups.sort((a, b) => b.atomIndices.length - a.atomIndices.length);

  return {
    groups,
    uniqueAtoms,
    totalAtoms: atomsToAnalyze.length,
  };
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Calculate distance between two 3D points
 */
function distance(p1: Vec3, p2: Vec3): number {
  const dx = p1[0] - p2[0];
  const dy = p1[1] - p2[1];
  const dz = p1[2] - p2[2];
  return Math.sqrt(dx * dx + dy * dy + dz * dz);
}

/**
 * Generate atom descriptors from atom data
 */
function generateDescriptors(atoms: AtomData[]): AtomDescriptor[] {
  return atoms.map(atom => ({
    index: atom.index,
    element: atom.element,
    atomicNumber: ATOMIC_NUMBERS[atom.element] ?? '00',
    position: atom.position,
    numBondsNotToH: 0,
    numBondsToH: 0,
    ringInfo: 0,
    oneThreeElem: 1,
    oneThreeCount: 1,
    oneFourElem: 1,
    oneFourCount: 1,
  }));
}

/**
 * Generate adjacency list based on atomic distances
 */
function generateAdjacencyList(
  descriptors: AtomDescriptor[]
): Map<number, Set<number>> {
  const adjList = new Map<number, Set<number>>();
  const n = descriptors.length;

  // Initialize empty adjacency sets
  for (let i = 0; i < n; i++) {
    adjList.set(i, new Set<number>());
  }

  // Check all pairs of atoms
  for (let i = 0; i < n; i++) {
    for (let j = i + 1; j < n; j++) {
      const atom1 = descriptors[i];
      const atom2 = descriptors[j];

      const dist = distance(atom1.position, atom2.position);

      const radius1 = getAtomicRadius(atom1.element);
      const radius2 = getAtomicRadius(atom2.element);
      const threshold = (radius1 + radius2) * BOND_THRESHOLD;

      if (dist > MIN_BOND_DISTANCE && dist < threshold) {
        adjList.get(i)!.add(j);
        adjList.get(j)!.add(i);
      }
    }
  }

  return adjList;
}

/**
 * Count bonds to hydrogen and non-hydrogen atoms
 */
function countBonds(
  descriptors: AtomDescriptor[],
  adjacencyList: Map<number, Set<number>>
): void {
  for (let i = 0; i < descriptors.length; i++) {
    const neighbors = adjacencyList.get(i);
    if (!neighbors) continue;

    for (const neighborIdx of neighbors) {
      const neighborElement = descriptors[neighborIdx].element;
      if (isHydrogen(neighborElement)) {
        descriptors[i].numBondsToH++;
      } else {
        descriptors[i].numBondsNotToH++;
      }
    }
  }
}

/**
 * Calculate ring membership for each atom using BFS
 * Returns the size of smallest ring containing each atom (0 if not in ring)
 */
function calculateRingInfo(
  descriptors: AtomDescriptor[],
  adjacencyList: Map<number, Set<number>>
): number[] {
  const n = descriptors.length;
  const ringInfo = new Array<number>(n).fill(0);

  for (let startNode = 0; startNode < n; startNode++) {
    const smallestRing = findSmallestRing(startNode, 8, adjacencyList);
    if (smallestRing > 0) {
      ringInfo[startNode] = smallestRing;
    }
  }

  return ringInfo;
}

/**
 * Find the smallest ring containing a given node using BFS
 */
function findSmallestRing(
  startNode: number,
  maxSize: number,
  adjacencyList: Map<number, Set<number>>
): number {
  const visited = new Map<number, number>();
  const parent = new Map<number, number>();
  const queue: Array<{ node: number; depth: number }> = [];

  visited.set(startNode, 0);
  parent.set(startNode, -1);
  queue.push({ node: startNode, depth: 0 });

  while (queue.length > 0) {
    const { node, depth } = queue.shift()!;

    if (depth >= maxSize) continue;

    const neighbors = adjacencyList.get(node);
    if (!neighbors) continue;

    for (const neighbor of neighbors) {
      if (neighbor === parent.get(node)) {
        continue;
      }

      if (visited.has(neighbor)) {
        const cycleSize = depth + 1 + visited.get(neighbor)!;
        if (cycleSize >= 3 && cycleSize <= maxSize) {
          return cycleSize;
        }
      } else {
        visited.set(neighbor, depth + 1);
        parent.set(neighbor, node);
        queue.push({ node: neighbor, depth: depth + 1 });
      }
    }
  }

  return 0;
}

/**
 * Calculate 1-3 element products (atoms separated by 2 bonds)
 */
function calculateOneThreeProducts(
  descriptors: AtomDescriptor[],
  adjacencyList: Map<number, Set<number>>
): void {
  const n = descriptors.length;

  for (const [_node, neighbors] of adjacencyList) {
    const neighborArray = Array.from(neighbors);

    for (let i = 0; i < neighborArray.length; i++) {
      for (let j = i + 1; j < neighborArray.length; j++) {
        const nodeI = neighborArray[i];
        const nodeJ = neighborArray[j];

        if (nodeI >= n || nodeJ >= n) continue;

        const primeI = getElementPrime(descriptors[nodeI].element);
        const primeJ = getElementPrime(descriptors[nodeJ].element);

        descriptors[nodeI].oneThreeElem *= primeJ;
        descriptors[nodeJ].oneThreeElem *= primeI;

        descriptors[nodeI].oneThreeCount++;
        descriptors[nodeJ].oneThreeCount++;
      }
    }
  }
}

/**
 * Calculate 1-4 element products (atoms separated by 3 bonds)
 */
function calculateOneFourProducts(
  descriptors: AtomDescriptor[],
  adjacencyList: Map<number, Set<number>>
): void {
  const n = descriptors.length;
  const flexibleElements = new Set(['S', 'P', 'N']);

  for (const [node, neighbors] of adjacencyList) {
    const neighborArray = Array.from(neighbors);

    for (let i = 0; i < neighborArray.length; i++) {
      for (let j = i + 1; j < neighborArray.length; j++) {
        const nodeI = neighborArray[i];
        const nodeJ = neighborArray[j];

        if (nodeI >= n || nodeJ >= n) continue;

        const elementI = descriptors[nodeI].element;
        const elementJ = descriptors[nodeJ].element;

        // For each neighbor k of nodeI (excluding central node)
        const neighborsOfI = adjacencyList.get(nodeI);
        if (neighborsOfI) {
          for (const k of neighborsOfI) {
            if (k !== node && k < n && !flexibleElements.has(elementJ)) {
              const primeK = getElementPrime(descriptors[k].element);
              descriptors[nodeJ].oneFourElem *= primeK;
              descriptors[nodeJ].oneFourCount++;
            }
          }
        }

        // For each neighbor l of nodeJ (excluding central node)
        const neighborsOfJ = adjacencyList.get(nodeJ);
        if (neighborsOfJ) {
          for (const l of neighborsOfJ) {
            if (l !== node && l < n && !flexibleElements.has(elementI)) {
              const primeL = getElementPrime(descriptors[l].element);
              descriptors[nodeI].oneFourElem *= primeL;
              descriptors[nodeI].oneFourCount++;
            }
          }
        }
      }
    }
  }
}

/**
 * Generate unique atom string for each atom
 *
 * Format: numBondsNotToH + atomicNumber + numBondsToH + ringInfo
 *         + sorted neighbor atomic numbers + oneThreeElem + oneThreeCount
 *         + oneFourElem + oneFourCount
 */
function generateAtomStrings(
  descriptors: AtomDescriptor[],
  adjacencyList: Map<number, Set<number>>
): string[] {
  const strings: string[] = [];

  for (let nodeIdx = 0; nodeIdx < descriptors.length; nodeIdx++) {
    const desc = descriptors[nodeIdx];

    let str = '';
    str += desc.numBondsNotToH.toString();
    str += desc.atomicNumber;
    str += desc.numBondsToH.toString();
    str += desc.ringInfo.toString();

    // Sorted neighbor atomic numbers
    const neighbors = adjacencyList.get(nodeIdx);
    if (neighbors && neighbors.size > 0) {
      const neighborAtomicNumbers = Array.from(neighbors)
        .filter(n => n < descriptors.length)
        .map(n => descriptors[n].atomicNumber)
        .sort();
      str += neighborAtomicNumbers.join('');
    }

    // 1-3 and 1-4 element products
    str += Math.floor(desc.oneThreeElem).toString();
    str += Math.floor(desc.oneThreeCount).toString();
    str += Math.floor(desc.oneFourElem).toString();
    str += Math.floor(desc.oneFourCount).toString();

    strings.push(str);
  }

  return strings;
}

// ============================================================================
// Ring Detection for SSS Ring Mode
// ============================================================================

/**
 * Detect all rings in a molecule (SSSR - Smallest Set of Smallest Rings)
 *
 * @param atoms - Array of atom data from 3Dmol viewer
 * @returns Ring information including all rings and ring bonds
 */
export function detectRings(atoms: AtomData[]): RingInfo {
  const descriptors = generateDescriptors(atoms);
  const adjacencyList = generateAdjacencyList(descriptors);

  const rings: number[][] = [];
  const atomToRings = new Map<number, number[]>();
  const ringBonds = new Set<string>();
  const foundRings = new Set<string>();

  // Initialize atomToRings for all atoms
  for (const atom of atoms) {
    atomToRings.set(atom.index, []);
  }

  // Find rings using DFS from each atom
  for (let startIdx = 0; startIdx < descriptors.length; startIdx++) {
    const ringsFromStart = findRingsFromNode(
      startIdx,
      8, // Max ring size
      descriptors,
      adjacencyList
    );

    for (const ring of ringsFromStart) {
      // Create canonical ring representation for deduplication
      const canonical = canonicalizeRing(ring);
      const ringKey = canonical.join(',');

      if (!foundRings.has(ringKey)) {
        foundRings.add(ringKey);
        const ringIndex = rings.length;

        // Map back to original atom indices
        const originalRing = ring.map(i => descriptors[i].index);
        rings.push(originalRing);

        // Update atomToRings
        for (const atomIdx of originalRing) {
          atomToRings.get(atomIdx)!.push(ringIndex);
        }

        // Add ring bonds
        for (let i = 0; i < originalRing.length; i++) {
          const a = originalRing[i];
          const b = originalRing[(i + 1) % originalRing.length];
          const bondKey = a < b ? `${a}-${b}` : `${b}-${a}`;
          ringBonds.add(bondKey);
        }
      }
    }
  }

  return { rings, atomToRings, ringBonds };
}

/**
 * Find all rings starting from a given node using DFS
 */
function findRingsFromNode(
  startNode: number,
  maxSize: number,
  _descriptors: AtomDescriptor[],
  adjacencyList: Map<number, Set<number>>
): number[][] {
  const rings: number[][] = [];
  const path: number[] = [startNode];
  const visited = new Set<number>([startNode]);

  function dfs(current: number, depth: number): void {
    if (depth > maxSize) return;

    const neighbors = adjacencyList.get(current);
    if (!neighbors) return;

    for (const neighbor of neighbors) {
      // Found a cycle back to start
      if (neighbor === startNode && depth >= 3) {
        rings.push([...path]);
        continue;
      }

      // Continue DFS if not visited
      if (!visited.has(neighbor) && neighbor > startNode) {
        visited.add(neighbor);
        path.push(neighbor);
        dfs(neighbor, depth + 1);
        path.pop();
        visited.delete(neighbor);
      }
    }
  }

  dfs(startNode, 1);
  return rings;
}

/**
 * Create canonical (rotated to start with smallest index) ring representation
 */
function canonicalizeRing(ring: number[]): number[] {
  if (ring.length === 0) return ring;

  // Find minimum index
  let minIdx = 0;
  for (let i = 1; i < ring.length; i++) {
    if (ring[i] < ring[minIdx]) {
      minIdx = i;
    }
  }

  // Rotate to start with minimum
  const rotated = [...ring.slice(minIdx), ...ring.slice(0, minIdx)];

  // Choose direction that gives smaller second element
  if (rotated.length > 1 && rotated[rotated.length - 1] < rotated[1]) {
    return [rotated[0], ...rotated.slice(1).reverse()];
  }

  return rotated;
}

// ============================================================================
// 3Dmol Color Function Factories
// ============================================================================

/**
 * Create a 3Dmol color function for symmetry groups
 *
 * @param symmetryResult - Result from detectSymmetry()
 * @returns Color function compatible with 3Dmol.js
 */
export function create3DmolSymmetryColorFunc(
  symmetryResult: SymmetryResult
): (atom: any) => number {
  // Build lookup from atom index to color
  const atomToColor = new Map<number, RGBColor>();

  for (const group of symmetryResult.groups) {
    for (const atomIdx of group.atomIndices) {
      atomToColor.set(atomIdx, group.color);
    }
  }

  return (atom: any) => {
    const serial = atom.serial - 1; // 3Dmol uses 1-based indexing
    const color = atomToColor.get(serial) ?? DEFAULT_ATOM_COLOR;

    const r = Math.round(color[0] * 255);
    const g = Math.round(color[1] * 255);
    const b = Math.round(color[2] * 255);

    return (r << 16) | (g << 8) | b;
  };
}

/**
 * Create a 3Dmol color function for ring highlighting
 *
 * @param ringInfo - Result from detectRings()
 * @param selectedRingIndex - Index of ring to highlight (-1 for all rings)
 * @returns Color function compatible with 3Dmol.js
 */
export function create3DmolRingColorFunc(
  ringInfo: RingInfo,
  selectedRingIndex: number = -1
): (atom: any) => number {
  return (atom: any) => {
    const serial = atom.serial - 1;
    const atomRings = ringInfo.atomToRings.get(serial);

    if (atomRings && atomRings.length > 0) {
      if (selectedRingIndex === -1) {
        // All rings mode - color by first ring membership
        const colorIdx = atomRings[0] % SYMMETRY_COLORS.length;
        const color = SYMMETRY_COLORS[colorIdx];
        const r = Math.round(color[0] * 255);
        const g = Math.round(color[1] * 255);
        const b = Math.round(color[2] * 255);
        return (r << 16) | (g << 8) | b;
      } else if (atomRings.includes(selectedRingIndex)) {
        // Specific ring selected
        const color = SYMMETRY_COLORS[selectedRingIndex % SYMMETRY_COLORS.length];
        const r = Math.round(color[0] * 255);
        const g = Math.round(color[1] * 255);
        const b = Math.round(color[2] * 255);
        return (r << 16) | (g << 8) | b;
      }
    }

    // Not in selected ring - grey
    return 0xc0c0c0;
  };
}

/**
 * Create a 3Dmol color function for ring bonds highlighting
 *
 * @param ringInfo - Result from detectRings()
 * @returns Color function that highlights atoms in rings
 */
export function create3DmolRingBondsColorFunc(
  ringInfo: RingInfo
): (atom: any) => number {
  return (atom: any) => {
    const serial = atom.serial - 1;
    const atomRings = ringInfo.atomToRings.get(serial);

    if (atomRings && atomRings.length > 0) {
      // Atom is in at least one ring - red
      return 0xc00000;
    }

    // Not in any ring - grey
    return 0xc0c0c0;
  };
}
